import { OPS } from "pdfjs-dist/legacy/build/pdf.mjs";

/**
 * Reads one pdf.js page into plain data the layout code (pdfLayout.ts) can work on: every piece of
 * text with its real position (measured from the top-left of the page as it is displayed, so
 * rotated pages work), real font style, real colour, link and form-field values. Nothing here
 * decides paragraphs or tables; that is pdfLayout.ts, which is pure and unit-tested.
 *
 * `page` is a pdf.js PDFPageProxy (any build). It is typed loosely on purpose so the tests can
 * hand in a page from the Node build while the app hands in one from the browser build.
 */

export interface TextItem {
  str: string;
  /** Left edge, in points from the left of the displayed page. */
  x: number;
  /** Baseline, in points from the TOP of the displayed page. */
  baseline: number;
  width: number;
  /** Font size in points. */
  size: number;
  bold: boolean;
  italic: boolean;
  /** "RRGGBB" without #; undefined for black/unknown. */
  color?: string;
  /** Word-friendly font name when the PDF's own font is a common one. */
  font?: string;
  link?: string;
  /** Text that did not come from the page's text layer (a filled-in form field). */
  synthetic?: boolean;
  /** Text that is not horizontal on the displayed page. */
  rotated?: boolean;
}

export interface PageContent {
  widthPt: number;
  heightPt: number;
  items: TextItem[];
  /** True when the page itself carries a text layer (form-field values alone do not count). */
  hasText: boolean;
  /** True when links and form-field values could not be read from this page (the text itself was). */
  annotationsFailed?: boolean;
}

/** Real bold/italic from the embedded font's own name (the old code read pdf.js's generic family, which never says "bold"). */
export function styleFromFontName(rawName: string, flags?: { bold?: boolean; italic?: boolean }): { bold: boolean; italic: boolean } {
  const name = rawName.replace(/^[A-Z]{6}\+/, "").toLowerCase();
  const bold = !!flags?.bold || /bold|heavy|black|semibold|demibold|extrabold|-bd\b|,bd\b/.test(name);
  const italic = !!flags?.italic || /italic|oblique|-it\b|,it\b|-ital\b/.test(name);
  return { bold, italic };
}

/** A font Word has, for the PDF's font, when it is one of the common families; undefined otherwise (Word's default is then used). */
export function wordFontFor(rawName: string): string | undefined {
  const n = rawName.replace(/^[A-Z]{6}\+/, "").toLowerCase().replace(/[\s_]/g, "");
  if (/arial|helvetica|liberationsans|arimo|nimbussans/.test(n)) return "Arial";
  if (/timesnewroman|times|liberationserif|tinos|nimbusroman/.test(n)) return "Times New Roman";
  if (/couriernew|courier|liberationmono|cousine|nimbusmono/.test(n)) return "Courier New";
  if (/calibri|carlito/.test(n)) return "Calibri";
  if (/cambria|caladea/.test(n)) return "Cambria";
  if (/verdana/.test(n)) return "Verdana";
  if (/tahoma/.test(n)) return "Tahoma";
  if (/georgia/.test(n)) return "Georgia";
  if (/trebuchet/.test(n)) return "Trebuchet MS";
  if (/segoeui/.test(n)) return "Segoe UI";
  if (/consolas/.test(n)) return "Consolas";
  if (/garamond/.test(n)) return "Garamond";
  if (/palatino|bookantiqua/.test(n)) return "Palatino Linotype";
  if (/centurygothic/.test(n)) return "Century Gothic";
  return undefined;
}

const hex2 = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");

/** Turns what pdf.js reports for a fill-colour operator into "rrggbb" ("#rrggbb" strings in current pdf.js; 0..1 or 0..255 numbers in older ones). */
export function colorFromOperatorArgs(op: number, args: unknown[]): string | undefined {
  const a0 = args?.[0];
  if (typeof a0 === "string") {
    const m = /^#?([0-9a-f]{6})$/i.exec(a0.trim());
    return m ? m[1].toLowerCase() : undefined;
  }
  const nums = (args ?? []).filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  if (op === OPS.setFillGray && nums.length >= 1) {
    const g = nums[0] <= 1 ? nums[0] * 255 : nums[0];
    return hex2(g) + hex2(g) + hex2(g);
  }
  if (op === OPS.setFillRGBColor && nums.length >= 3) {
    const scale = nums.every((n) => n <= 1) ? 255 : 1;
    return hex2(nums[0] * scale) + hex2(nums[1] * scale) + hex2(nums[2] * scale);
  }
  if (op === OPS.setFillCMYKColor && nums.length >= 4) {
    const [c, m, y, k] = nums;
    return hex2(255 * (1 - c) * (1 - k)) + hex2(255 * (1 - m) * (1 - k)) + hex2(255 * (1 - y) * (1 - k));
  }
  return undefined;
}

type Mat = [number, number, number, number, number, number];
const applyPt = (m: Mat, x: number, y: number): [number, number] => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
/** Bounding box of a user-space rectangle after the viewport transform: [left, top, right, bottom]. */
export function viewportRect(m: Mat, rect: number[]): [number, number, number, number] {
  const pts = [applyPt(m, rect[0], rect[1]), applyPt(m, rect[2], rect[1]), applyPt(m, rect[2], rect[3]), applyPt(m, rect[0], rect[3])];
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

const isWs = (ch: string) => /\s/.test(ch);

/**
 * Walks the page's drawing commands and records, for every visible character, the fill colour that
 * was active when it was drawn (colour is part of the graphics state, so q/Q save and restore it).
 * Returns one colour per non-space character, in drawing order; the caller matches them to text
 * items by counting characters, and only trusts the result when the totals agree.
 */
export function collectCharColors(opList: { fnArray: number[]; argsArray: any[] }): (string | undefined)[] {
  const out: (string | undefined)[] = [];
  const stack: (string | undefined)[] = [];
  let color: string | undefined;
  for (let i = 0; i < opList.fnArray.length; i++) {
    const fn = opList.fnArray[i];
    const args = opList.argsArray[i];
    if (fn === OPS.save) stack.push(color);
    else if (fn === OPS.restore) color = stack.pop();
    else if (fn === OPS.setFillGray || fn === OPS.setFillRGBColor || fn === OPS.setFillCMYKColor) color = colorFromOperatorArgs(fn, args ?? []);
    else if (fn === OPS.setFillColorSpace || fn === OPS.setFillColor || fn === OPS.setFillColorN) {
      // Pattern / special colour spaces: not trusted, fall back to the default colour.
      color = undefined;
    } else if (fn === OPS.showText || (OPS as any).showSpacedText === fn) {
      const glyphs = args?.[0];
      if (!Array.isArray(glyphs)) continue;
      const c = color === "000000" ? undefined : color;
      for (const g of glyphs) {
        if (g && typeof g === "object" && typeof g.unicode === "string") {
          for (const ch of Array.from(g.unicode as string)) if (!isWs(ch)) out.push(c);
        }
      }
    }
  }
  return out;
}

const countVisible = (s: string) => Array.from(s).filter((ch) => !isWs(ch)).length;

/** Matches the per-character colour list to text items; undefined when the counts disagree (then no colour is applied rather than a wrong one). */
export function colorsForItems(strs: string[], charColors: (string | undefined)[]): (string | undefined)[] | undefined {
  const total = strs.reduce((n, s) => n + countVisible(s), 0);
  if (total !== charColors.length) return undefined;
  const res: (string | undefined)[] = [];
  let pos = 0;
  for (const s of strs) {
    const n = countVisible(s);
    res.push(n > 0 ? charColors[pos] : undefined);
    pos += n;
  }
  return res;
}

/** Text of a form field as it should read in the Word file; "" for fields with nothing worth showing. */
export function formFieldText(a: any): string {
  if (!a || a.subtype !== "Widget" || a.pushButton) return "";
  const v = a.fieldValue;
  if (a.checkBox) {
    const on = v !== undefined && v !== null && v !== "" && v !== "Off" && v !== false;
    return on ? "☒" : "☐";
  }
  if (a.radioButton) {
    const on = v !== undefined && v !== null && v !== "Off" && v === a.buttonValue;
    return on ? "◉" : "○";
  }
  if (Array.isArray(v)) return v.filter((x) => typeof x === "string" && x).join(", ");
  return typeof v === "string" ? v.replace(/\r\n|\r|\n/g, " ").trim() : "";
}

/** Reads the page. See the file comment. */
export async function readPageContent(page: any): Promise<PageContent> {
  const viewport = page.getViewport({ scale: 1 });
  const vt = viewport.transform as Mat;
  const widthPt: number = viewport.width;
  const heightPt: number = viewport.height;

  let annotationsFailed = false;
  const [content, opList, annotations] = await Promise.all([
    page.getTextContent(),
    page.getOperatorList(),
    page.getAnnotations({ intent: "display" }).catch((err: unknown) => {
      annotationsFailed = true;
      console.warn("readPageContent: couldn't read links and form fields", err);
      return [] as any[];
    }),
  ]);

  const fontCache = new Map<string, { bold: boolean; italic: boolean; font?: string }>();
  const fontInfo = (fontName: string) => {
    let info = fontCache.get(fontName);
    if (info) return info;
    let rawName = "";
    let flags: { bold?: boolean; italic?: boolean } = {};
    try {
      const f = page.commonObjs.has(fontName) ? page.commonObjs.get(fontName) : null;
      if (f) {
        rawName = String(f.name ?? "");
        flags = { bold: !!f.bold, italic: !!f.italic };
      }
    } catch {
      /* font object not available: style stays plain */
    }
    const style = styleFromFontName(rawName, flags);
    info = { ...style, font: rawName ? wordFontFor(rawName) : undefined };
    fontCache.set(fontName, info);
    return info;
  };

  type Raw = { it: any; str: string };
  const raws: Raw[] = (content.items as any[])
    .filter((it) => typeof it.str === "string" && Array.isArray(it.transform))
    .map((it) => ({ it, str: it.str as string }));

  // Colour: only trusted when the characters drawn and the characters read agree in number.
  const colors = colorsForItems(raws.map((r) => r.str), collectCharColors(opList));

  const items: TextItem[] = [];
  raws.forEach((r, idx) => {
    if (r.str.trim().length === 0) return;
    const tr = r.it.transform as number[];
    const [ox, oy] = applyPt(vt, tr[4], tr[5]);
    // Direction of the text on the displayed page.
    const dx = vt[0] * tr[0] + vt[2] * tr[1];
    const dy = vt[1] * tr[0] + vt[3] * tr[1];
    const size = Math.hypot(tr[2], tr[3]) || Math.hypot(tr[0], tr[1]) || 10;
    const rotated = Math.abs(dy) > 0.08 * Math.abs(dx) || dx < 0;
    const fi = fontInfo(r.it.fontName);
    items.push({
      str: r.str,
      x: ox,
      baseline: oy,
      width: typeof r.it.width === "number" ? r.it.width : 0,
      size,
      bold: fi.bold,
      italic: fi.italic,
      font: fi.font,
      color: colors?.[idx],
      rotated: rotated || undefined,
    });
  });
  const hasText = items.length > 0;

  // Links and filled-in form fields.
  for (const a of annotations as any[]) {
    if (!a?.rect) continue;
    const [l, t, r, b] = viewportRect(vt, a.rect);
    if (a.subtype === "Link") {
      const url: string | undefined = a.url || a.unsafeUrl;
      if (!url || !/^(https?:|mailto:|tel:)/i.test(url)) continue;
      for (const it of items) {
        if (it.rotated || it.synthetic) continue;
        const iLeft = it.x;
        const iRight = it.x + it.width;
        const overlap = Math.min(iRight, r + 1) - Math.max(iLeft, l - 1);
        const vOverlap = it.baseline >= t - 2 && it.baseline - it.size * 0.7 <= b + 2;
        if (vOverlap && overlap > 0 && overlap >= Math.min(it.width, r - l) * 0.5) it.link = url;
      }
    } else if (a.subtype === "Widget") {
      const text = formFieldText(a);
      if (!text) continue;
      const h = b - t;
      const size = Math.max(8, Math.min(11, h * 0.7));
      items.push({
        str: text,
        x: l + 2,
        baseline: t + h / 2 + size * 0.32,
        width: text.length * size * 0.5,
        size,
        bold: false,
        italic: false,
        synthetic: true,
      });
    }
  }

  return { widthPt, heightPt, items, hasText, annotationsFailed: annotationsFailed || undefined };
}
